import { useCallback, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';
import type { OperatorTaskDetailsResponse } from '@/core/generated/api';
import { isBadRequestError } from '@/core/api/badRequestError';
import { operatorTaskService, OperatorTaskConflictError } from '@/entities/OperatorTask/api/operatorTaskService';
import { formToCreateTask, formToUpdateTask } from '@/entities/OperatorTask/mappers';
import type { OperatorTaskFormValues } from '@/entities/OperatorTask/model/schema';
import { getErrorMessage } from '@/shared/lib/errors';
import { useOperatorTasks } from '@/entities/OperatorTask/model/store';
import type { OperatorTaskOrigin } from '@/entities/OperatorTask/model/types';

/** Сохраняет задачу общей очереди */
export const useOperatorTaskSubmit = (
    form: UseFormReturn<OperatorTaskFormValues>,
    onSaved: (task: OperatorTaskDetailsResponse) => void,
    original?: OperatorTaskDetailsResponse,
    origin?: OperatorTaskOrigin,
) => {
    const pending = useRef(false);

    const creation = useRef({
        payload: '',
        key: crypto.randomUUID()
    });

    const [conflict, setConflict] = useState(false);

    const onSubmit = useCallback(async (values: OperatorTaskFormValues) => {
        if (pending.current || (original && !form.formState.isDirty) || conflict) return;

        pending.current = true;
        form.clearErrors('root.server');

        try {
            const payload = JSON.stringify(formToCreateTask(values, '', origin));

            if (creation.current.payload && creation.current.payload !== payload) creation.current.key = crypto.randomUUID();

            creation.current.payload = payload;

            const response = original?.id
                ? await operatorTaskService.update(original.id, formToUpdateTask(values, original))
                : await operatorTaskService.create(formToCreateTask(values, creation.current.key, origin));

            toast.success(original
                ? 'Задача обновлена'
                : 'Задача создана');

            form.reset(values);
            useOperatorTasks.getState().invalidateNotifications();

            if (response.task) onSaved(response.task);
        } catch (error: unknown) {
            if (error instanceof OperatorTaskConflictError) setConflict(true);
            if (!isBadRequestError(error)) {
                form.setError('root.server', {
                    type: 'server',
                    message: getErrorMessage(error, 'Не удалось сохранить задачу'),
                });
            }
        } finally {
            pending.current = false;
        }
    }, [conflict, form, onSaved, original, origin]);

    return {
        onSubmit,
        conflict
    };
};
